import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { gutters } from '../../../app/spacing';
import { TextEllipsis } from '../../Kizen/Table';
import ButtonGroupButton from '../ButtonGroup/Button';
import { hasInputControl } from '../InputControl';
import { selectedValueProp } from '../props';

export const minValueRating = 5;
export const maxValueRating = 10;

const BottomLabelsWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  & > p {
    margin-top: 0;
    width: calc((100% - ${gutters.spacing(4)}px) / 2);
  }

  & > p:last-child {
    text-align: right;
  }
`;

const TopLabelsWrapper = styled.div`
  display: flex;
  margin-bottom: ${gutters.spacing(2, -2)}px;

  p {
    width: calc((100% - ${gutters.spacing(4)}px) / 2);
  }
`;

const ButtonLayout = styled.div`
  display: grid;
  margin-bottom: ${gutters.spacing(2, 1)}px;
  ${({ maxRating }) => css`
    grid-template-columns: repeat(${maxRating}, minmax(25px, 36px));
  `}
  column-gap: ${gutters.spacing(2)}px;
  ${({ hasSecondRow, maxRating }) =>
    hasSecondRow &&
    maxRating === maxValueRating &&
    css`
      grid-template-columns: repeat(5, minmax(25px, 36px));
      grid-template-rows: repeat(2, 1fr);
      row-gap: ${gutters.spacing(2)}px;
    `}

  & > * {
    display: inline-block;
  }
`;

const OutlineRating = React.forwardRef(function OutlineRating(props, ref) {
  const {
    value,
    onChange,
    error,
    disabled,
    className,
    maxRating,
    hasSecondRow,
    rating,
    lowLabel,
    highLabel,
    options,
    required,
  } = props;
  const showSecondRow = hasSecondRow && maxRating === maxValueRating;

  return (
    <div ref={ref}>
      {showSecondRow && (
        <TopLabelsWrapper>
          <TextEllipsis type="micro">
            {rating?.minLabel || lowLabel}
          </TextEllipsis>
        </TopLabelsWrapper>
      )}
      <ButtonLayout
        className={!hasInputControl(props) && className}
        maxRating={maxRating}
        hasSecondRow={hasSecondRow}
      >
        {options.map((opt) => {
          const selected = Boolean(value && value.value === opt.value);
          return (
            <ButtonGroupButton
              option={opt}
              key={opt.value}
              selected={selected}
              error={error}
              disabled={disabled}
              onClick={(ev) => {
                if (onChange) {
                  if (selected && !required) {
                    ev.currentTarget.blur();
                    onChange(null, ev);
                  } else {
                    onChange(opt, ev);
                  }
                }
              }}
            />
          );
        })}
      </ButtonLayout>
      <BottomLabelsWrapper>
        {!showSecondRow ? (
          <TextEllipsis type="micro" style={{ height: 'calc(1em + 2px)' }}>
            {rating?.minLabel || lowLabel}
          </TextEllipsis>
        ) : (
          <div />
        )}
        <TextEllipsis type="micro" style={{ height: 'calc(1em + 2px)' }}>
          {rating?.maxLabel || highLabel}
        </TextEllipsis>
      </BottomLabelsWrapper>
    </div>
  );
});

OutlineRating.propTypes = {
  value: selectedValueProp,
  onChange: PropTypes.func,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  lowLabel: PropTypes.string,
  highLabel: PropTypes.string,
  hasSecondRow: PropTypes.bool.isRequired,
  maxRating: PropTypes.number.isRequired,
  options: PropTypes.array.isRequired,
};

OutlineRating.defaultProps = {
  value: null,
  onChange: null,
  error: null,
  disabled: null,
  lowLabel: 'Unsatisfied',
  highLabel: 'Satisfied',
};

export default Object.assign(OutlineRating, {
  // eslint-disable-next-line react/forbid-foreign-prop-types
  propTypes: OutlineRating.propTypes,
  defaultProps: OutlineRating.defaultProps,
});
